import React, { useEffect, useRef, useState } from 'react';
import { Card, Upload, Button, message, Row, Col, Input, Tag, Progress, Space, Select, notification } from 'antd';
import api, { getAvailableEngines, getBaiduTaskStatus } from '../services/api';
import { UploadOutlined, PlusOutlined, DeleteOutlined, FolderOpenOutlined, ArrowUpOutlined, ArrowDownOutlined, StarOutlined } from '@ant-design/icons';
import { useQuickTags } from '../utils/useQuickTags';
import './UploadPage.css';

const { TextArea } = Input;

const UploadPage: React.FC = () => {
  const [fileList, setFileList] = useState<any[]>([]);
  const fullFileListRef = useRef<any[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentBatch, setCurrentBatch] = useState(0);
  const [totalBatches, setTotalBatches] = useState(0);
  const [descriptions, setDescriptions] = useState<{[key: string]: string}>({});
  const [tags, setTags] = useState<{[key: string]: string[]}>({});
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [engine, setEngine] = useState<string>('baidu_cloud');
  const [engines, setEngines] = useState<string[]>(['default']);
  const [notifApi, notifCtx] = notification.useNotification();
  const SHOW_BAIDU_PROGRESS = false;
  const MAX_FILES = 500;
  const SAFE_PREVIEW_LIMIT = 200;
  const [previewLimited, setPreviewLimited] = useState(false);
  const [previewTotal, setPreviewTotal] = useState(0);
  const [globalBarDescription, setGlobalBarDescription] = useState('');
  const [globalBarTags, setGlobalBarTags] = useState('');
  // 全局可管理标签（与移动端保持一致）
  const [managedTags, setManagedTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [quickTags, quickActions] = useQuickTags();

  const applyGlobalBarToAll = () => {
    if (fileList.length === 0) {
      message.warning('请先选择图片');
      return;
    }
    const desc = (globalBarDescription || '').trim();
    const tagsArrInput = (globalBarTags || '').split(',').map(t => t.trim()).filter(Boolean);
    const useTags = (managedTags.length ? managedTags : tagsArrInput);
    if (!desc && useTags.length === 0) {
      message.warning('请填写全局描述或标签');
      return;
    }
    if (desc) {
      setDescriptions(prev => {
        const next = { ...prev } as { [key: string]: string };
        fileList.forEach(f => { next[f.uid] = desc; });
        return next;
      });
    }
    if (useTags.length) {
      setTags(prev => {
        const next = { ...prev } as { [key: string]: string[] };
        fileList.forEach(f => { next[f.uid] = useTags.slice(); });
        return next;
      });
    }
    message.success('已将全局描述与标签应用到全部图片');
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getAvailableEngines();
        if (data?.engines?.length) {
          setEngines(data.engines);
          if (data.engines.includes('baidu_cloud')) {
            setEngine('baidu_cloud');
          } else if (!data.engines.includes(engine)) {
            setEngine(data.engines[0]);
          }
        }
      } catch (e) {
        // ignore
      }
    })();
  }, []);

  // 处理文件上传
  const handleFileChange = (info: any) => {
    const maxFiles = MAX_FILES;
    if (info.fileList.length > maxFiles) {
      message.warning(`最多只能选择${maxFiles}张图片`);
      return;
    }
  console.log('Ant Upload文件变化:', info.fileList);
  try {
    const processed = (info.fileList || []).map((f: any, idx: number) => {
      const needUrl = !f?.url && f?.originFileObj && (f.originFileObj instanceof File || f.originFileObj instanceof Blob);
      let url = f?.url as string | undefined;
      if (needUrl) {
        try { url = URL.createObjectURL(f.originFileObj as File); } catch {}
      }
      return { ...f, uid: f.uid || `file-${Date.now()}-${idx}`, url };
    });
    fullFileListRef.current = processed;
    if (processed.length > SAFE_PREVIEW_LIMIT) {
      setPreviewLimited(true);
      setPreviewTotal(processed.length);
      setFileList(processed.slice(0, SAFE_PREVIEW_LIMIT));
    } else {
      setPreviewLimited(false);
      setPreviewTotal(processed.length);
      setFileList(processed);
    }
  } catch {
    setFileList(info.fileList);
  }
    
    // 初始化描述和标签（默认描述：若有 webkitRelativePath，取首层文件夹名）
    info.fileList.forEach((file: any) => {
      const rel: string = file.webkitRelativePath || '';
      const defaultDesc = (rel && rel.includes('/')) ? rel.split('/')[0] : '';
      if (!descriptions[file.uid]) {
        setDescriptions(prev => ({
          ...prev,
          [file.uid]: defaultDesc
        }));
      }
      if (!tags[file.uid]) {
        setTags(prev => ({
          ...prev,
          [file.uid]: []
        }));
      }
    });
  };

  // 处理描述输入
  const handleDescriptionChange = (uid: string, value: string) => {
    console.log('Description change:', uid, value); // 调试日志
    setDescriptions(prev => ({
      ...prev,
      [uid]: value
    }));
  };

  // 处理标签输入  
  const handleTagsChange = (uid: string, value: string) => {
    console.log('Tag change:', uid, value); // 调试日志
    // 简化处理，直接存储输入的字符串，在上传时再处理
    setTags(prev => ({
      ...prev,
      [uid]: value ? value.split(',').map(tag => tag.trim()).filter(tag => tag) : []
    }));
  };

  // 处理文件夹上传
  const handleFolderUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // 改进图片文件检测，同时检查MIME类型和文件扩展名
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.tif'];
    const imageFiles = Array.from(files).filter(file => {
      // 首先检查MIME类型
      if (file.type && file.type.startsWith('image/')) {
        return true;
      }
      // 如果MIME类型为空，检查文件扩展名
      const fileName = file.name.toLowerCase();
      return imageExtensions.some(ext => fileName.endsWith(ext));
    });

    console.log(`文件夹中共有 ${files.length} 个文件，其中 ${imageFiles.length} 个是图片文件`);

    if (imageFiles.length === 0) {
      message.warning('所选文件夹中没有找到图片文件');
      return;
    }

    if (imageFiles.length > MAX_FILES) {
      message.warning(`文件夹中图片数量过多，最多支持${MAX_FILES}张图片`);
      return;
    }

    const newFileList = imageFiles.map((file, index) => {
      console.log(`处理文件 ${index}:`, {
        name: file.name,
        type: file.type,
        size: file.size,
        isFile: file instanceof File
      });
      
      let url: string | undefined = undefined;
      try { url = URL.createObjectURL(file); } catch {}
      return {
        uid: `folder-${Date.now()}-${index}`,
        name: file.name,
        status: 'done' as const,
        originFileObj: file,
        webkitRelativePath: (file as any).webkitRelativePath || file.name,
        size: file.size,
        type: file.type,
        url
      };
    });

    // 添加到现有文件列表
    const combinedList = [...(fullFileListRef.current || []), ...newFileList];
    if (combinedList.length > MAX_FILES) {
      message.warning(`总文件数量不能超过${MAX_FILES}张`);
      return;
    }

    fullFileListRef.current = combinedList;
    if (combinedList.length > SAFE_PREVIEW_LIMIT) {
      setPreviewLimited(true);
      setPreviewTotal(combinedList.length);
      setFileList(combinedList.slice(0, SAFE_PREVIEW_LIMIT));
    } else {
      setPreviewLimited(false);
      setPreviewTotal(combinedList.length);
      setFileList(combinedList);
    }

    // 初始化描述和标签
    newFileList.forEach((file: any) => {
      setDescriptions(prev => ({
        ...prev,
        [file.uid]: ''
      }));
      setTags(prev => ({
        ...prev,
        [file.uid]: []
      }));
    });

    message.success(`成功添加${imageFiles.length}张图片`);
    
    // 清除input值，允许重复选择同一文件夹
    if (folderInputRef.current) {
      folderInputRef.current.value = '';
    }
  };

  // 点击文件夹按钮
  const handleFolderButtonClick = () => {
    folderInputRef.current?.click();
  };

  // 客户端压缩：最长边≤1400，JPEG质量85
  const compressImage = (file: File, maxSide = 1400, quality = 0.85): Promise<File> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(file);
        const { width, height } = img;
        const scale = Math.min(1, maxSide / Math.max(width, height));
        const w = Math.max(1, Math.round(width * scale));
        const h = Math.max(1, Math.round(height * scale));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          if (!blob) return resolve(file);
          const name = (file.name || 'image').toLowerCase().endsWith('.jpg') || (file.name || 'image').toLowerCase().endsWith('.jpeg')
            ? file.name
            : (file.name ? file.name.replace(/\.[^.]+$/, '') : 'image') + '.jpg';
          resolve(new File([blob], name, { type: 'image/jpeg' }));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  // 简单并发执行器
  const runWithConcurrency = async <T,>(tasks: Array<() => Promise<T>>, limit = 3): Promise<T[]> => {
    const results: T[] = new Array(tasks.length);
    let cursor = 0;
    const workers = new Array(Math.min(limit, tasks.length)).fill(0).map(async () => {
      while (true) {
        const current = cursor++;
        if (current >= tasks.length) break;
        results[current] = await tasks[current]();
      }
    });
    await Promise.all(workers);
    return results;
  };

  // 开始上传（客户端压缩 + 分批并发上传）
  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的图片');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // 分批并行：每批 batchSize，批内并发 concurrency
      const batchSize = 5;
      const concurrency = 4;
      let completed = 0;
      const total = (fullFileListRef.current && fullFileListRef.current.length) ? fullFileListRef.current.length : fileList.length;
      const batches = Math.ceil(total / batchSize);
      setTotalBatches(batches);

      const createdIds: number[] = [];
      const all = (fullFileListRef.current && fullFileListRef.current.length) ? fullFileListRef.current : fileList;
      for (let b = 0; b < batches; b += 1) {
        setCurrentBatch(b + 1);
        const slice = all.slice(b * batchSize, (b + 1) * batchSize);
        const tasks: Array<() => Promise<any>> = slice.map((fileItem) => {
          return async () => {
            let fileObj: File | null = null as any;
            if (fileItem.originFileObj && (fileItem.originFileObj instanceof File || fileItem.originFileObj instanceof Blob)) {
              fileObj = fileItem.originFileObj as File;
            } else if (fileItem instanceof File) {
              fileObj = fileItem as unknown as File;
            } else if ((fileItem as any).file && ((fileItem as any).file instanceof File || (fileItem as any).file instanceof Blob)) {
              fileObj = (fileItem as any).file as File;
            }
            if (!fileObj) return { ok: false, error: '无效文件' };

            const compressed = await compressImage(fileObj);
            const fd = new FormData();
            fd.append('file', compressed, compressed.name);
            fd.append('engine', engine);
            // 放宽：描述/标签可为空
            if ((descriptions[fileItem.uid] || '').trim()) {
              fd.append('description', descriptions[fileItem.uid].trim());
            }
            const joinedTags = (tags[fileItem.uid] || []).join(',');
            if (joinedTags.trim()) {
              fd.append('tags', joinedTags.trim());
            }

            const resp = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
            if (resp?.data?.image_id) createdIds.push(resp.data.image_id);
            completed += 1;
            setUploadProgress(Math.min(100, Math.round((completed / total) * 100)));
            return { ok: true, data: resp.data };
          };
        });
        await runWithConcurrency(tasks, concurrency);
      }
      message.success(`上传完成：成功 ${total} / 共 ${total}`);
      // 不再跳转，避免白屏；由用户自行进入图片管理查看
      // 顶部微通知：百度后台同步进度
      if (createdIds.length > 0) {
        const key = 'baidu-sync-upload';
        notifApi.open({ key, message: '百度后台同步', description: `正在同步 0/${createdIds.length}`, duration: 0 });
        let tries = 0;
        const timer = window.setInterval(async () => {
          tries += 1;
          try {
            const res = await getBaiduTaskStatus(createdIds);
            const statuses = res.statuses || {} as Record<number, any>;
            let done = 0, err = 0;
            Object.values(statuses).forEach((s: any) => {
              if (!s) return;
              if (s.status === 'done') done += 1; else if (s.status === 'error') err += 1;
            });
            const totalAll = createdIds.length;
            const running = Math.max(0, totalAll - done - err);
            notifApi.open({ key, message: '百度后台同步', description: `完成 ${done}/${totalAll}，失败 ${err}，进行中 ${running}` , duration: 0 });
            if (done + err >= totalAll || tries >= 20) {
              window.clearInterval(timer);
              if (err === 0) {
                notifApi.success({ key, message: '百度后台同步完成', description: `全部 ${totalAll} 已完成`, duration: 2 });
              } else {
                notifApi.warning({ key, message: '百度后台同步结束', description: `完成 ${done}/${totalAll}，失败 ${err}` , duration: 3 });
              }
            }
          } catch (e) {
            // 忽略一次失败
            if (tries >= 20) {
              window.clearInterval(timer);
              notifApi.destroy(key);
            }
          }
        }, 1500);
      }
      try {
        const all = (fullFileListRef.current && fullFileListRef.current.length) ? fullFileListRef.current : fileList;
        all.forEach((f: any) => { if (f?.url && typeof f.url === 'string' && f.url.startsWith('blob:')) URL.revokeObjectURL(f.url); });
      } catch {}
      setFileList([]);
      fullFileListRef.current = [];
      setDescriptions({});
      setTags({});
      setUploadProgress(100);
      setTimeout(() => setUploadProgress(0), 800);
    } catch (error) {
      message.error('上传失败，请检查网络连接');
    } finally {
      setUploading(false);
      setCurrentBatch(0);
      setTotalBatches(0);
    }
  };

  // 移除文件
  const handleRemove = (file: any) => {
    try { if (file?.url && typeof file.url === 'string' && file.url.startsWith('blob:')) URL.revokeObjectURL(file.url); } catch {}
    setFileList(prev => prev.filter(f => f.uid !== file.uid));
    // 清除对应的描述和标签
    setDescriptions(prev => {
      const newDescriptions = { ...prev };
      delete newDescriptions[file.uid];
      return newDescriptions;
    });
    setTags(prev => {
      const newTags = { ...prev };
      delete newTags[file.uid];
      return newTags;
    });
  };

  return (
    <div className="upload-page">
      {SHOW_BAIDU_PROGRESS && notifCtx}
      <Card title="图片上传" className="upload-card">
        {/* 上传区域 */}
        <div className="upload-section">
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Space size="middle">
                <div>
                  <span style={{ marginRight: 8 }}>识别引擎：</span>
                  <Select value={engine} onChange={setEngine} style={{ width: 200 }}>
                    {engines.map(e => (
                      <Select.Option key={e} value={e}>{e}</Select.Option>
                    ))}
                  </Select>
                </div>
                <Button 
                  icon={<FolderOpenOutlined />}
                  onClick={handleFolderButtonClick}
                  type="dashed"
                >
                  选择文件夹
                </Button>
                <span style={{ color: '#666', fontSize: '14px' }}>
                  或单独选择图片文件
                </span>
              </Space>
              {previewLimited && (
                <div style={{ marginTop: 8, color: '#fa8c16' }}>
                  预览已限制为前 {SAFE_PREVIEW_LIMIT} 张，共选择 {previewTotal} 张。其余不会预览，但仍可逐批上传。
                </div>
              )}
            </div>
            
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={(file) => {
                console.log('beforeUpload:', file);
                return false; // 阻止自动上传
              }}
              onRemove={handleRemove}
              showUploadList={false}
              accept="image/*"
              multiple
            >
              {fileList.length >= MAX_FILES ? null : (
                <div className="upload-button">
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>选择图片</div>
                </div>
              )}
            </Upload>
          </Space>
          
          {/* 隐藏的文件夹输入 */}
          <input
            type="file"
            ref={folderInputRef}
            onChange={handleFolderUpload}
            {...{webkitdirectory: "", directory: ""} as any}
            multiple
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>

        {/* 文件信息编辑 */}
        {fileList.length > 0 && (
          <div className="file-info-section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="title-icon">📝</span>
                图片信息设置
                <span className="file-count">({fileList.length} 张图片)</span>
              </h3>
              <p className="section-description">
                为您的图片添加描述和标签，便于后续管理和搜索
              </p>
            </div>
            {/* 全局描述/标签 应用条 */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0 16px' }}>
              <Input
                placeholder="全局描述（一次性覆盖全部）"
                value={globalBarDescription}
                onChange={(e) => setGlobalBarDescription(e.target.value)}
                style={{ maxWidth: 360 }}
              />
              <Input
                placeholder="全局标签（逗号分隔，一次性覆盖全部）"
                value={globalBarTags}
                onChange={(e) => setGlobalBarTags(e.target.value)}
                style={{ maxWidth: 400 }}
              />
              <Button type="primary" onClick={applyGlobalBarToAll}>应用到全部</Button>
            </div>
            {/* 全局标签可管理区域（新增，与移动端一致的操作体验） */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '0 0 16px' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Input
                  placeholder="输入标签后回车/添加（可多次添加）"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onPressEnter={() => {
                    const parts = (newTag || '').split(',').map(s => s.trim()).filter(Boolean);
                    if (!parts.length) return;
                    setManagedTags(prev => {
                      const seen = new Set(prev);
                      const merged = [...prev];
                      parts.forEach(p => { if (p && !seen.has(p)) { seen.add(p); merged.push(p); } });
                      return merged;
                    });
                    setNewTag('');
                  }}
                  style={{ maxWidth: 320 }}
                />
                <Button onClick={() => {
                  const parts = (newTag || '').split(',').map(s => s.trim()).filter(Boolean);
                  if (!parts.length) return;
                  setManagedTags(prev => {
                    const seen = new Set(prev);
                    const merged = [...prev];
                    parts.forEach(p => { if (p && !seen.has(p)) { seen.add(p); merged.push(p); } });
                    return merged;
                  });
                  setNewTag('');
                }}>添加</Button>
                <Button onClick={() => setManagedTags([])}>清空</Button>
                <Button type="link" onClick={() => quickActions.addMany(managedTags)}>保存为常用</Button>
              </div>
              {/* 常用标签（可点击追加） */}
              {quickTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {quickTags.map(tag => (
                    <span
                      key={tag}
                      onClick={() => setManagedTags(prev => prev.includes(tag) ? prev : [...prev, tag])}
                      style={{
                        fontSize: 12,
                        color: '#4096ff',
                        background: '#f0f8ff',
                        border: '1px solid #b3d8ff',
                        padding: '4px 8px',
                        borderRadius: 12,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >{tag}</span>
                  ))}
                </div>
              )}
              {/* 已选全局标签（可调序/删除/设为常用） */}
              {managedTags.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 12, color: '#595959', fontWeight: 600 }}>已选全局标签：</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {managedTags.map((t, idx) => (
                      <span key={`${t}-${idx}`} style={{ fontSize: 12, color: '#fff', background: '#4096ff', border: '1px solid #b3d8ff', padding: '4px 8px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {t}
                        <span style={{ display: 'inline-flex', gap: 4 }}>
                          <ArrowUpOutlined onClick={() => setManagedTags(prev => { if (idx === 0) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx-1,0,x); return c; })} />
                          <ArrowDownOutlined onClick={() => setManagedTags(prev => { if (idx === prev.length-1) return prev; const c = prev.slice(); const [x] = c.splice(idx,1); c.splice(idx+1,0,x); return c; })} />
                          <DeleteOutlined onClick={() => setManagedTags(prev => prev.filter((_, i) => i !== idx))} />
                          <StarOutlined onClick={() => quickActions.add(t)} />
                        </span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Row gutter={[16, 16]}>
              {fileList.map(file => (
                <Col span={24} lg={12} key={file.uid}>
                  <Card size="small" className="file-info-card">
                    <Row gutter={16}>
                      <Col span={8}>
                        <div style={{ 
                          width: '100%', 
                          height: '120px', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          border: '1px solid #f0f0f0',
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={file.url} 
                            alt={file.name}
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              borderRadius: '6px'
                            }}
                          />
                        </div>
                      </Col>
                      <Col span={16}>
                        <div className="file-info">
                          <div className="file-header">
                            <h4 className="file-title">{file.name}</h4>
                            {file.webkitRelativePath && file.webkitRelativePath !== file.name && (
                              <div className="file-path">
                                <span className="path-label">路径:</span>
                                <span className="path-text">{file.webkitRelativePath}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="form-fields">
                            <div className="field-group" style={{position: 'relative', zIndex: 200}}>
                              <label className="field-label">
                                <span className="label-icon">📝</span>
                                <span className="label-text">图片描述</span>
                              </label>
                              <textarea
                                value={descriptions[file.uid] || ''}
                                onChange={(e) => handleDescriptionChange(file.uid, e.target.value)}
                                placeholder="请简要描述图片内容、用途或特点..."
                                rows={3}
                                maxLength={200}
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #d9d9d9',
                                  resize: 'none',
                                  fontSize: '14px',
                                  lineHeight: '1.5',
                                  outline: 'none',
                                  pointerEvents: 'auto',
                                  position: 'relative',
                                  zIndex: 201
                                }}
                                onFocus={(e) => {
                                  console.log('Native textarea focused:', file.uid);
                                  e.target.style.borderColor = '#40a9ff';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#d9d9d9';
                                }}
                              />
                              <div style={{fontSize: '12px', color: '#999', textAlign: 'right', marginTop: '4px'}}>
                                {(descriptions[file.uid] || '').length}/200
                              </div>
                            </div>
                            
                            <div className="field-group" style={{position: 'relative', zIndex: 200}}>
                              <label className="field-label">
                                <span className="label-icon">🏷️</span>
                                <span className="label-text">瓷砖类型</span>
                              </label>
                              <input
                                type="text"
                                value={tags[file.uid]?.join(', ') || ''}
                                onChange={(e) => handleTagsChange(file.uid, e.target.value)}
                                placeholder="输入瓷砖类型，用逗号分隔，如：抛釉砖, 仿古砖, 木纹砖"
                                style={{
                                  width: '100%',
                                  padding: '8px 12px',
                                  borderRadius: '6px',
                                  border: '1px solid #d9d9d9',
                                  fontSize: '14px',
                                  outline: 'none',
                                  pointerEvents: 'auto',
                                  position: 'relative',
                                  zIndex: 201
                                }}
                                onFocus={(e) => {
                                  console.log('Native input focused:', file.uid);
                                  e.target.style.borderColor = '#40a9ff';
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = '#d9d9d9';
                                }}
                              />
                              <div className="quick-tags">
                                <span className="quick-tags-label">快速标签：</span>
                                <div className="quick-tags-list">
                                  {['抛釉砖', '全抛釉', '仿古砖', '大理石纹', '木纹砖', '水泥灰', '哑光', '亮光', '通体砖', '微晶石', '马赛克', '地面砖', '墙砖', '瓷片', '仿石纹'].map(tag => (
                                    <span 
                                      key={tag}
                                      className="quick-tag"
                                      onClick={() => {
                                        const currentTags = tags[file.uid] || [];
                                        if (!currentTags.includes(tag)) {
                                          setTags(prev => ({
                                            ...prev,
                                            [file.uid]: [...currentTags, tag]
                                          }));
                                        }
                                      }}
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <div className="input-hint">
                                💡 点击快速标签添加，或手动输入自定义标签
                              </div>
                            </div>
                            
                            {tags[file.uid] && tags[file.uid].length > 0 && (
                              <div className="tags-preview">
                                <div className="tags-preview-label">标签预览：</div>
                                <div className="tags-container">
                                  {tags[file.uid].map((tag, index) => (
                                    <Tag key={index} color="processing" className="preview-tag">
                                      {tag}
                                    </Tag>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* 上传进度 */}
        {uploading && (
          <div className="upload-progress">
            <Progress percent={uploadProgress} status="active" />
            <p>正在上传图片...</p>
          </div>
        )}

        {/* 上传按钮 */}
        {fileList.length > 0 && (
          <div className="upload-actions">
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={handleUpload}
              loading={uploading}
              size="large"
              disabled={uploading}
            >
              开始上传 ({fileList.length} 张图片)
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default UploadPage;
